import { useFeatureGate, Feature, Tier } from '@/hooks/useFeatureGate'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Lock, ArrowRight, Check } from 'lucide-react'
import { TierBadge, STANDARD_TIER_MONTHLY_CENTS, formatMonthly } from '@/components/ui/TierBadge'
import { useNavigate } from 'react-router-dom'

/**
 * Human-readable blurbs per feature. Shown in the upgrade modal so the user
 * sees what they're unlocking, not just a feature slug.
 */
const FEATURE_COPY: Record<Feature, { title: string; blurb: string; sellingPoints: string[] }> = {
  ai_inbound_agent: {
    title: 'AI inbound voice agent',
    blurb: 'Answer every call instantly, 24/7. No voicemail — your intake happens the moment someone picks up the phone.',
    sellingPoints: ['1 voice agent + local number', '250 calls/mo included', 'Call transcripts & summaries'],
  },
  phone_number: {
    title: 'Phone numbers',
    blurb: 'A dedicated number your AI answers on, or your existing number routed to the agent.',
    sellingPoints: ['1 local number included', 'Call recording + transcripts', 'Transfer to live intake'],
  },
  call_transcripts: {
    title: 'Call transcripts',
    blurb: 'Full searchable transcripts of every inbound call with speaker labels.',
    sellingPoints: ['Per-call transcripts', 'Caller history', 'AI summaries'],
  },
  call_summaries: {
    title: 'Call summaries',
    blurb: 'Post-call AI summaries with next steps and sentiment.',
    sellingPoints: ['Auto-summary per call', 'Sentiment scoring', 'Lead qualification score'],
  },
  caller_history: {
    title: 'Caller history',
    blurb: 'See every previous touchpoint with a caller before you pick up.',
    sellingPoints: ['Cross-call caller timeline', 'Linked to chat history'],
  },
  ai_outbound_agent: {
    title: 'AI outbound agent',
    blurb: 'Speed-to-lead: when someone fills out a web form, the AI calls them back instantly — before another firm reaches them. Also re-engages callers who dropped off mid-intake.',
    sellingPoints: ['Instant callback on web-form leads', 'Recovers dropped inbound calls', 'Configurable call windows'],
  },
  auto_callback: {
    title: 'Auto-callback',
    blurb: 'If an inbound call drops or the caller hangs up mid-intake, the AI dials them back on a smart retry schedule so the lead doesn\'t get lost.',
    sellingPoints: ['Recovers dropped inbound calls', 'Smart timing (business hours only)', 'Configurable retry schedule'],
  },
  auto_follow_up: {
    title: 'Auto follow-up',
    blurb: 'Keep prospects warm between first call and consultation — the AI handles the pings.',
    sellingPoints: ['Scheduled touchpoints', 'Drop-off recovery', 'Drip sequences'],
  },
  reminder_calls: {
    title: 'Appointment reminders',
    blurb: 'Night-before and same-day reminder calls for consultations.',
    sellingPoints: ['Configurable timing', 'SMS fallback', 'No-show tracking'],
  },
  outreach_lists: {
    title: 'Outreach lists',
    blurb: 'Load a list of leads and let the AI work through them.',
    sellingPoints: ['CSV upload', 'Calling windows', 'Progress tracking'],
  },
  crm_integrations: {
    title: 'CRM integrations',
    blurb: 'Two-way sync with Clio, Lawmatics, HubSpot, Salesforce, and more.',
    sellingPoints: ['Auto-sync bookings & calls', 'Contact dedup', 'Field mapping'],
  },
  priority_support: {
    title: 'Priority support',
    blurb: 'Fast-track support and a direct line to our team.',
    sellingPoints: ['Same-day response', 'Dedicated onboarding', 'Quarterly review calls'],
  },
  // Baseline features — should never land in the upgrade modal, but keep typed
  ai_chat_agent: { title: 'AI chat agent', blurb: '', sellingPoints: [] },
  knowledge_base: { title: 'Knowledge base', blurb: '', sellingPoints: [] },
  lead_scoring: { title: 'Lead scoring', blurb: '', sellingPoints: [] },
  booking_calendar: { title: 'Booking calendar', blurb: '', sellingPoints: [] },
  chat_transcripts: { title: 'Chat transcripts', blurb: '', sellingPoints: [] },
  human_takeover_chat: { title: 'Human takeover', blurb: '', sellingPoints: [] },
}

const TIER_BLURB: Record<Tier, string> = {
  associate: 'Chat intake only.',
  partner: 'Chat + inbound voice. Every call answered 24/7.',
  principal: 'Full intake + speed-to-lead outbound. AI calls web-form leads back instantly and recovers dropped calls.',
}

function priceDelta(currentTier: string | null, requiredTier: Tier): string {
  const currentCents =
    currentTier && STANDARD_TIER_MONTHLY_CENTS[currentTier]
      ? STANDARD_TIER_MONTHLY_CENTS[currentTier]
      : 0
  const requiredCents = STANDARD_TIER_MONTHLY_CENTS[requiredTier] ?? 0
  const diff = requiredCents - currentCents
  if (diff <= 0) return formatMonthly(requiredCents)
  return `+${formatMonthly(diff)}/mo`
}

/**
 * Global upgrade prompt. Mounted once at the app root; driven by
 * useFeatureGate().upgradePrompt, which is set by the API client's 402
 * interceptor (automatic) OR by direct requestUpgrade() calls from UI
 * components (explicit click on locked feature).
 */
export function UpgradeModal() {
  const { upgradePrompt, closeUpgrade, tier } = useFeatureGate()
  const navigate = useNavigate()

  if (!upgradePrompt) return null
  const { feature, requiredTier } = upgradePrompt
  const copy = FEATURE_COPY[feature] ?? { title: feature, blurb: '', sellingPoints: [] }

  const goToBilling = () => {
    closeUpgrade()
    navigate('/settings/billing')
  }

  return (
    <Dialog open={!!upgradePrompt} onOpenChange={(o) => !o && closeUpgrade()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-brand-royal/10 flex items-center justify-center">
              <Lock className="h-4 w-4 text-brand-royal" />
            </div>
            <div>
              <DialogTitle className="text-lg">Upgrade to unlock</DialogTitle>
              <DialogDescription className="text-xs">
                This feature is included on the <TierBadge tier={requiredTier} size="sm" className="mx-1 inline-flex" /> plan.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <h3 className="text-base font-semibold">{copy.title}</h3>
            {copy.blurb && <p className="text-sm text-muted-foreground mt-1">{copy.blurb}</p>}
          </div>

          {copy.sellingPoints.length > 0 && (
            <ul className="space-y-1.5 rounded-lg bg-muted/30 p-3">
              {copy.sellingPoints.map((sp) => (
                <li key={sp} className="flex items-start gap-2 text-sm">
                  <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
                  <span>{sp}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="rounded-lg border p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{TIER_BLURB[requiredTier]}</span>
              <span className="font-semibold">{priceDelta(tier, requiredTier)}</span>
            </div>
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <Button variant="ghost" onClick={closeUpgrade} className="flex-1">
            Maybe later
          </Button>
          <Button
            onClick={goToBilling}
            className="flex-1 bg-gradient-to-r from-brand-royal to-brand-periwinkle hover:brightness-110 text-white gap-2"
          >
            Upgrade to <span className="capitalize">{requiredTier}</span>
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
